<script setup lang="ts">
// One notification row - severity dot, title, optional body (2-line clamp),
// relative time, an optional CTA link, and a dismiss icon-button. Shared by
// the bell dropdown and the full-page NotificationsView. Unread rows carry a
// subtle left accent + stronger background. Clicking the row (or the CTA)
// marks it read via the store; the dismiss button removes it.
import AppIcon from '@/components/AppIcon.vue';
import { computed } from 'vue';
import { relativeTime } from '@/lib/relativeTime';
import { useNotificationsStore, type Notification } from '@/stores/notifications';
import { useI18n } from '@/lib/i18n';
import { Tooltip } from '@hq/chat-ui';

const { t } = useI18n();
const props = defineProps<{ notification: Notification }>();

const notifications = useNotificationsStore();

const isUnread = computed(() => props.notification.status === 'unread');
const when = computed(() => relativeTime(props.notification.created_at));

const cta = computed(() => props.notification.cta);
const ctaLabel = computed(() => cta.value?.label || t('notifications.cta_open'));
// Internal paths (start with "/") route in-app; anything else is an external
// anchor opened in a new tab.
const ctaIsInternal = computed(
  () => typeof cta.value?.href === 'string' && cta.value.href.startsWith('/'),
);

function onActivate(): void {
  void notifications.markRead(props.notification.id);
}

function onDismiss(e: MouseEvent): void {
  e.stopPropagation();
  void notifications.dismiss(props.notification.id);
}
</script>

<template>
  <div
    class="notif"
    :class="[`notif--${notification.severity}`, { 'notif--unread': isUnread }]"
    tabindex="0"
    @click="onActivate"
    @keydown.enter="onActivate"
    @keydown.space.prevent="onActivate"
  >
    <span class="notif__dot" aria-hidden="true" />
    <div class="notif__body">
      <p class="notif__title">{{ notification.title }}</p>
      <p v-if="notification.body" class="notif__text">{{ notification.body }}</p>
      <div class="notif__foot">
        <span class="notif__time">{{ when }}</span>
        <RouterLink
          v-if="cta && ctaIsInternal"
          :to="cta.href!"
          class="notif__cta"
          @click="onActivate"
        >{{ ctaLabel }}</RouterLink>
        <a
          v-else-if="cta && cta.href"
          :href="cta.href"
          class="notif__cta"
          target="_blank"
          rel="noopener noreferrer"
          @click="onActivate"
        >{{ ctaLabel }}</a>
      </div>
    </div>
    <Tooltip :label="t('notifications.dismiss')">
      <button
        type="button"
        class="notif__dismiss"
        :aria-label="t('notifications.dismiss')"
        @click="onDismiss"
      >
        <AppIcon name="x" :size="15" />
      </button>
    </Tooltip>
  </div>
</template>

<style scoped lang="scss">
@layer surface {
  .notif {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-3) var(--space-3) var(--space-4);
    border-radius: var(--radius-input);
    cursor: pointer;
    transition: background var(--dur-1) var(--ease-out);

    &:hover { background: var(--surface-sunken); }

    // Unread: a subtle left accent bar + a slightly warmer panel fill so
    // it reads above the muted read rows.
    &--unread {
      background: var(--surface-panel);
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: var(--space-2);
        bottom: var(--space-2);
        width: 2px;
        border-radius: var(--radius-chip);
        background: var(--accent-default);
      }
      .notif__title { color: var(--ink-primary); font-weight: 600; }
    }

    // Severity drives the dot colour via the status tokens.
    &--info    .notif__dot { background: var(--status-info); }
    &--success .notif__dot { background: var(--status-success); }
    &--warning .notif__dot { background: var(--status-warn); }
  }

  .notif__dot {
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    margin-top: 6px;
    border-radius: var(--radius-pill);
  }

  .notif__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .notif__title {
    margin: 0;
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--ink-primary);
    line-height: 1.35;
  }
  .notif__text {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--ink-muted);
    line-height: var(--leading-base);
    // Clamp to ~2 lines so a long body doesn't blow out the dropdown.
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .notif__foot {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-top: 2px;
  }
  .notif__time {
    font-size: var(--text-xs);
    color: var(--ink-subtle);
  }
  .notif__cta {
    font-size: var(--text-xs);
    font-weight: 600;
    color: var(--accent-default);
    text-decoration: none;

    &:hover { text-decoration: underline; }
    &:focus-visible {
      outline: 2px solid var(--border-focus);
      outline-offset: 2px;
      border-radius: var(--radius-chip);
    }
  }

  .notif__dismiss {
    flex-shrink: 0;
    display: inline-grid;
    place-items: center;
    width: 26px;
    height: 26px;
    border: 0;
    border-radius: var(--radius-input);
    background: transparent;
    color: var(--ink-subtle);
    cursor: pointer;
    opacity: 0;
    transition:
      opacity var(--dur-1) var(--ease-out),
      background var(--dur-1) var(--ease-out),
      color var(--dur-1) var(--ease-out);

    &:hover { background: var(--surface-sunken); color: var(--ink-primary); }
    &:focus-visible {
      opacity: 1;
      outline: 2px solid var(--border-focus);
      outline-offset: -2px;
    }
  }
  // Reveal dismiss on row hover/focus; keep it always visible on touch
  // (no hover) so it stays reachable.
  .notif:hover .notif__dismiss,
  .notif:focus-within .notif__dismiss { opacity: 1; }
  @media (hover: none) {
    .notif__dismiss { opacity: 1; }
  }
}
</style>
